import { useContext, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { GeaoContext } from "../../../GeaoContextProvider";
import { isCobrandedSubAgency } from "../../../mappers/InterviewMapper";
import { classes } from "../../../../../util/style-utils";
import { ClientConfigurationContext } from "../../../../../context/ClientConfigurationContextProvider";

/**
 * CobrandedCommissionsAcknowledgement component displays an acknowledgement statement
 * for cobranded sub-agencies confirming that commissions will be paid to the National Account.
 * 
 * This component only renders when the agency interview has a CobrandedNationalAccount value,
 * indicating the agency is a cobranded sub-agency.
 * 
 * @component
 * @returns {JSX.Element|null} The acknowledgement checkbox or null if not a cobranded sub-agency
 */
export default function CobrandedCommissionsAcknowledgement() {
    const { agencyInterview } = useContext(GeaoContext);
    const clientConfiguration = useContext(ClientConfigurationContext);
    const { register, formState: { errors } } = useFormContext();

    /**
     * The National Account display name from the agency interview.
     * Maps the code value (e.g., "IronpeakServicesCorp") to the display label (e.g., "Iron Peak").
     */
    const cobrandedNationalAccountName = useMemo(
        () => {
            const value = agencyInterview?.questionAnswers?.CobrandedNationalAccount?.answer;
            const option = clientConfiguration?.cobrandedNationalAccounts?.find(opt => opt.value === value);
            return option?.label ?? value;
        },
        [agencyInterview, clientConfiguration]
    );

    /**
     * The sub-agency name (current agency name) from the interview.
     */
    const subAgencyName = useMemo(
        () => agencyInterview?.questionAnswers?.AgencyName?.answer ?? "your agency",
        [agencyInterview]
    );

    // Only render for cobranded sub-agencies
    if (!isCobrandedSubAgency(agencyInterview)) {
        return null;
    }

    return (
        <div className="margin-top margin-bottom">
            <h3 className="margin-bottom">Commissions Acknowledgement</h3>

            <p className="margin-bottom" style={{ fontWeight: 'bold' }}>
                I agree and acknowledge that {cobrandedNationalAccountName}, is the proper recipient of any
                commissions payable, with respect to such business submitted to GEICO by {subAgencyName}.
            </p>

            <div
                className={classes("form-field", errors.cobrandedCommissionsAgreement && "form-field--error")}
            >
                <div className="checkbox-wrapper">
                    <div>
                        <input data-onboarding-checkbox-id="PrincipalFlow:AgencyOfficeInformation:CobrandedCommissionsAcknowledgement:AcceptCommissionAgreement"
                            id="cobranded-commissions-agreement"
                            type="checkbox"
                            {...register("cobrandedCommissionsAgreement")}
                            aria-describedby={errors.cobrandedCommissionsAgreement ? "cobranded-commissions-agreement-error" : undefined}
                        />
                        <label htmlFor="cobranded-commissions-agreement" className="checkbox">
                            I understand and agree to the above statement.<span className="required">*</span>
                        </label>
                    </div>
                    {errors.cobrandedCommissionsAgreement && (
                        <span id="cobranded-commissions-agreement-error" className="form-message" role="alert">
                            {errors.cobrandedCommissionsAgreement.message}
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
}
